"use client"

import React, { useState, useEffect } from "react"
import { Plus, X } from "lucide-react"
import { Button } from "../../../../shared/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../../shared/components/ui/dialog"
import { Input } from "../../../../shared/components/ui/input"
import StickySaveBar from "../../../../shared/components/sticky-save-bar"
import errorToast from "../../../../shared/lib/toast/error.toast"
import successToast from "../../../../shared/lib/toast/success.toast"
import FormCard from "../../ui/form-card"
import { useWorkScope } from "../../hooks/use-work-scopes"
import { sortToolsWithToolKitFirst } from "../../constants/tool-kit-contents"
import { stripBlankLineNames } from "../../utils/tools-supplies-blank-lines"

interface ToolsSuppliesManagerProps {
    workOrderType: string
    regionId?: number | null
    regionName?: string | null
    /** When true, hides the standalone page header (parent provides context). */
    embedded?: boolean
    scopeLabel?: string
    scopeActive?: boolean
    onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void
    onSaved?: () => void
}

function ToolsSuppliesManager({
    workOrderType,
    regionId,
    regionName,
    embedded = false,
    scopeLabel: scopeLabelProp,
    scopeActive = true,
    onUnsavedChangesChange,
    onSaved,
}: ToolsSuppliesManagerProps) {
    const { workScope, loading, error } = useWorkScope(workOrderType, regionId)

    const [tools, setTools] = useState<string[]>([])
    const [supplies, setSupplies] = useState<string[]>([])
    const [originalTools, setOriginalTools] = useState<string[]>([])
    const [originalSupplies, setOriginalSupplies] = useState<string[]>([])
    const [isAddModalOpen, setIsAddModalOpen] = useState(false)
    const [modalType, setModalType] = useState<"tool" | "supply">("tool")
    const [newItemInput, setNewItemInput] = useState("")
    const [isSaving, setIsSaving] = useState(false)
    const [showSuccessBar, setShowSuccessBar] = useState(false)

    const scopeLabel =
        scopeLabelProp ||
        workScope?.workScopeDisplay ||
        workScope?.workScope ||
        workOrderType

    useEffect(() => {
        if (workScope) {
            const rawTools = workScope.toolsNeeded || []
            const rawSupplies = workScope.suppliesNeeded || []
            const loadedTools = sortToolsWithToolKitFirst(stripBlankLineNames(rawTools))
            const loadedSupplies = stripBlankLineNames(rawSupplies)

            setTools(loadedTools)
            setSupplies(loadedSupplies)
            setOriginalTools(sortToolsWithToolKitFirst(rawTools))
            setOriginalSupplies(rawSupplies)
        }
    }, [workScope])

    const hasUnsavedChanges =
        JSON.stringify(tools) !== JSON.stringify(originalTools) ||
        JSON.stringify(supplies) !== JSON.stringify(originalSupplies)

    useEffect(() => {
        onUnsavedChangesChange?.(hasUnsavedChanges)
    }, [hasUnsavedChanges, onUnsavedChangesChange])

    useEffect(() => {
        if (hasUnsavedChanges && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [hasUnsavedChanges, showSuccessBar])

    const handleAddItem = () => {
        if (!newItemInput.trim()) {
            errorToast("Please enter an item name")
            return
        }

        const newItem = newItemInput.trim()

        if (modalType === "tool") {
            if (tools.includes(newItem)) {
                errorToast("This tool already exists")
                return
            }
            setTools(sortToolsWithToolKitFirst([...tools, newItem]))
        } else {
            if (supplies.includes(newItem)) {
                errorToast("This supply already exists")
                return
            }
            setSupplies([...supplies, newItem])
        }

        setIsAddModalOpen(false)
        setNewItemInput("")
    }

    const handleDeleteTool = (toolToDelete: string) => {
        const filtered = tools.filter(tool => tool !== toolToDelete)
        setTools(sortToolsWithToolKitFirst(filtered))
    }

    const handleDeleteSupply = (supplyToDelete: string) => {
        setSupplies(supplies.filter(supply => supply !== supplyToDelete))
    }

    const openAddModal = (type: "tool" | "supply") => {
        setModalType(type)
        setNewItemInput("")
        setIsAddModalOpen(true)
    }

    const handleSave = async () => {
        if (!workScope) return

        if (!regionId) {
            errorToast("Select a region before saving")
            return
        }

        try {
            setIsSaving(true)

            const toolsToSave = sortToolsWithToolKitFirst(tools)
            const suppliesToSave = [...supplies]

            const response = await fetch('/api/work-scopes/region-tools-supplies', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    regionId,
                    workScopeId: workScope.id,
                    toolsNeeded: toolsToSave,
                    suppliesNeeded: suppliesToSave,
                }),
            })

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}))
                throw new Error(
                    (payload as { error?: string }).error ||
                        'Failed to update work scope'
                )
            }

            setTools(toolsToSave)
            setSupplies(suppliesToSave)
            setOriginalTools(toolsToSave)
            setOriginalSupplies(suppliesToSave)

            setShowSuccessBar(true)
            successToast("Tools and supplies updated successfully")
            onSaved?.()

            setTimeout(() => {
                setShowSuccessBar(false)
            }, 3000)
        } catch (err) {
            errorToast(
                err instanceof Error ? err.message : "Failed to save changes"
            )
            console.error("Error saving:", err)
        } finally {
            setIsSaving(false)
        }
    }

    if (!regionId) {
        return (
            <FormCard>
                <div className="text-center py-12 text-muted-foreground">
                    <p>Select a region to manage default tools and supplies.</p>
                </div>
            </FormCard>
        )
    }

    if (loading) {
        return (
            <FormCard>
                <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </FormCard>
        )
    }

    if (error || !workScope) {
        return (
            <FormCard>
                <div className="text-center py-12">
                    <p className="text-red-500">Error loading work scope data</p>
                </div>
            </FormCard>
        )
    }

    return (
        <>
            {!embedded && (
                <div className="space-y-1 px-1">
                    <h4 className="text-base font-semibold">
                        {regionName ? `${regionName} — ` : ''}
                        {scopeLabel}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                        Default tools and supplies for new work orders in this region.
                    </p>
                </div>
            )}

            {embedded && !scopeActive && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    This work scope is inactive. Defaults will not appear when creating work orders until it is enabled.
                </div>
            )}

            <FormCard>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground px-1">
                            Default Tools
                        </h3>
                        <FormCard className="p-4">
                            <div className="space-y-1">
                                {tools.map((tool, index) => {
                                    const isNew = !originalTools.includes(tool)
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between py-1 px-2 rounded transition-colors group"
                                        >
                                            <span className={`text-sm ${isNew ? 'text-purple-700 font-medium' : 'text-foreground'}`}>
                                                {tool}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteTool(tool)}
                                                className="text-muted-foreground hover:text-red-600 p-0.5 rounded transition-colors"
                                                title="Remove tool"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )
                                })}
                                {tools.length === 0 && (
                                    <p className="text-muted-foreground text-sm text-center py-4">
                                        No tools added yet
                                    </p>
                                )}
                            </div>

                            <div className="pt-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openAddModal("tool")}
                                    className="w-full"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Tool
                                </Button>
                            </div>
                        </FormCard>
                    </div>

                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground px-1">
                            Default Supplies
                        </h3>
                        <FormCard className="p-4">
                            <div className="space-y-1">
                                {supplies.map((supply, index) => {
                                    const isNew = !originalSupplies.includes(supply)
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between py-1 px-2 rounded transition-colors group"
                                        >
                                            <span className={`text-sm ${isNew ? 'text-purple-700 font-medium' : 'text-foreground'}`}>
                                                {supply}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteSupply(supply)}
                                                className="text-muted-foreground hover:text-red-600 p-0.5 rounded transition-colors"
                                                title="Remove supply"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )
                                })}
                                {supplies.length === 0 && (
                                    <p className="text-muted-foreground text-sm text-center py-4">
                                        No supplies added yet
                                    </p>
                                )}
                            </div>

                            <div className="pt-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openAddModal("supply")}
                                    className="w-full"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Supply
                                </Button>
                            </div>
                        </FormCard>
                    </div>
                </div>
            </FormCard>

            <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Add New {modalType === "tool" ? "Tool" : "Supply"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Input
                            placeholder={`Enter ${modalType} name...`}
                            value={newItemInput}
                            onChange={(e) => setNewItemInput(e.currentTarget.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault()
                                    handleAddItem()
                                }
                            }}
                            autoFocus
                        />
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddModalOpen(false)
                                    setNewItemInput("")
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddItem}
                                className="flex-1"
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <StickySaveBar
                isVisible={hasUnsavedChanges || showSuccessBar}
                isSuccess={showSuccessBar}
                isSubmitting={isSaving}
                onSave={handleSave}
            />
        </>
    )
}

export default ToolsSuppliesManager
